import { useCallback, useEffect, useRef } from "react";
import { type NavigateOptions, useSearchParams } from "react-router";
import {
  enqueueSearchParamRemovals,
  enqueueSearchParamWrite,
  peekQueuedSearchParamValue,
  scheduleSearchParamsFlush,
} from "./searchParamsWriteQueue";

export type valueSetterType = (
  newValue?: number | string | null | undefined,
  options?: NavigateOptions
) => void;

type ValueToggleType = (options?: NavigateOptions) => Record<string, string>;

type UseSearchParamStateReturn<V = string | null | undefined | boolean> = [
  value: V,
  setValue: valueSetterType,
  toggleValue: ValueToggleType,
  removeValue: () => void,
] & {
  value: V;
  setSearchParam: valueSetterType;
  toggleSearchParam: ValueToggleType;
  removeSearchParam: () => void;
};

type BooleanValueSetterType = (newValue: boolean, options?: NavigateOptions) => void;

type UseBooleanSearchParamReturn = [
  boolean,
  ValueToggleType,
  BooleanValueSetterType,
];

const REMOVE_NAVIGATE_OPTIONS: NavigateOptions = {
  replace: true,
  preventScrollReset: true,
};

/**
 * `window.location.search` is the ONLY source-of-truth
 * Returns the current search params as an object
 */
export const getCurrentSearchParams = (): URLSearchParams =>
  new URLSearchParams(window.location.search);

/**
 * Returns the current search params as an object
 */
export const getCurrentSearchParamsObj = (): Record<string, string> =>
  Object.fromEntries(getCurrentSearchParams().entries());

/**
 * Value the pending batch will land on: its queued entry when it has one (`null` for a
 * queued removal), otherwise the address bar. Reading `window.location` alone misses
 * everything queued in this tick.
 */
const readPendingOrLiveValue = (paramName: string): string | null => {
  const queued = peekQueuedSearchParamValue(paramName);

  return queued === undefined ? getCurrentSearchParams().get(paramName) : queued;
};

/**
 * React Router bakes each render's `searchParams` into `setSearchParams`, so a writer
 * memoized with empty deps hands its updater a `prev` frozen at mount. The merge helper
 * back-fills from `prev`, which then resurrects every param removed since mount (closing
 * a drawer would bring a long-cleared `viewMode=compare` back). The ref keeps the writer
 * identity stable (see the ⚠️ below) while the snapshot stays current.
 */
const useLatestSetSearchParams = () => {
  const [, setSearchParams] = useSearchParams();
  const setSearchParamsRef = useRef(setSearchParams);

  setSearchParamsRef.current = setSearchParams;

  return setSearchParamsRef;
};

/**
 * Returns a function to remove specific search-params
 */
export const useRemoveSearchParams = () => {
  const setSearchParamsRef = useLatestSetSearchParams();

  return useCallback((paramNames?: (string | undefined)[] | undefined) => {
    const names = (paramNames ?? []).filter((name): name is string => Boolean(name));
    if (!names.length) return;

    // Same queue as the setters: a removal and a set landing in one tick are ordered
    // here instead of racing as two navigations.
    enqueueSearchParamRemovals(names, REMOVE_NAVIGATE_OPTIONS);
    scheduleSearchParamsFlush(() => setSearchParamsRef.current);
  }, []);
};

/**
 * On unmount after a real pathname change, removes given query keys (StrictMode-safe: same pathname skip).
 */
export const useClearSearchParamsOnPathnameChange = (
  paramNames: readonly string[]
) => {
  const removeSearchParams = useRemoveSearchParams();

  useEffect(() => {
    const mountedPathname = window.location.pathname;
    return () => {
      if (window.location.pathname === mountedPathname) return;
      removeSearchParams([...paramNames]);
    };
  }, [removeSearchParams]);
};

/**
 * Returns the value of the search param, a setter function to set the value, and a function to toggle the value
 *
 * If `paramName` is not provided, returns an array with the default value and a setter function to set the value.
 * This scenario is using an internal state to store the value, and not syncing with the URL search params.
 *
 * For a control that should also be remembered after the param is stripped (tab leave, reload),
 * use `createPersistedSearchParams` from `src/storage/persistedSearchParams` instead.
 *
 * ⚠️ - ALL HOOKS IN THIS FILE SHOULD NOT HAVE DEPENDENCIES AS THEY MUST REMAIN STABLE
 */
export const useSearchParamState = <V = string | null | undefined | boolean>(
  paramName?: string,
  defaultValue: string | boolean | null = null,
  globOptions?: NavigateOptions
): UseSearchParamStateReturn<V> => {
  const [searchParams] = useSearchParams();
  const setSearchParamsRef = useLatestSetSearchParams();
  const removeValue = useRemoveSearchParams();

  const value = paramName ? searchParams.get(paramName) : undefined;

  // https://api.reactrouter.com/v7/types/react_router.SetURLSearchParams.html
  const setValue: valueSetterType = useCallback((newValue, options) => {
    if (!paramName) {
      return;
    }

    if (newValue || newValue === 0) {
      // Skip if the batch already lands on this value — avoids racing with other URL
      // writers (e.g. components that call setSearchParams directly) that may have
      // already advanced the URL between this enqueue and the eventual flush, by which
      // point our queued value would be stale and clobber theirs.
      const stringValue = newValue + "";
      if (readPendingOrLiveValue(paramName) === stringValue) {
        return;
      }

      enqueueSearchParamWrite(paramName, stringValue, {
        ...globOptions,
        ...options,
      });
      scheduleSearchParamsFlush(() => setSearchParamsRef.current);
    } else {
      removeValue([paramName]);
    }
  }, []);

  const toggleValue = useCallback((options?: NavigateOptions) => {
    const current = getCurrentSearchParamsObj();

    if (readPendingOrLiveValue(paramName!) === "true") {
      // remove the param
      removeValue([paramName]);
    } else {
      setValue("true", { ...globOptions, ...options });
    }
    return current;
  }, []);

  const removeValueParam = useCallback(() => {
    removeValue([paramName]);
  }, [paramName]);

  // On mount, write `defaultValue` into the URL when this param is missing.
  // "Missing" has to mean the browser address bar, not only React Router.
  // After a redirect (for example "See details" resolving `latest` to a real
  // run), React Router can still say the param is empty while the address bar
  // already has it — `toolbox=insights`. Writing the default then would
  // replace that value (`insights` becomes `details`) even though the user
  // never picked the default. `readPendingOrLiveValue` reads the address bar,
  // including a write queued in this tick that has not landed yet.
  useEffect(() => {
    if (
      value === null &&
      defaultValue &&
      paramName &&
      !readPendingOrLiveValue(paramName)
    ) {
      // Seeding the default normalizes the URL, it is not a user action — pushing here
      // leaves a history entry whose only difference is a value the user never chose.
      setValue(defaultValue + "", { ...globOptions, replace: true });
    }
  }, []);

  // 4th slot is the no-arg remover (same as `removeSearchParam`): the name-taking
  // `removeValue` would silently no-op when destructured and called without arguments.
  return Object.assign([value, setValue, toggleValue, removeValueParam], {
    value,
    setSearchParam: setValue,
    toggleSearchParam: toggleValue,
    removeSearchParam: removeValueParam,
  }) as UseSearchParamStateReturn<V>;
};

/**
 * Returns the value of the search param
 */
export const useSearchParamValue = <V = string | null | undefined | boolean>(
  paramName: string
): V => useSearchParamState<V>(paramName)[0];

/**
 * Returns a function to set the value of the search param
 */
export const useSetSearchParam = (
  paramName: string,
  globOptions?: NavigateOptions
): valueSetterType => useSearchParamState(paramName, null, globOptions)[1];

/**
 * Returns a function to toggle the value of the search param
 */
export const useToggleSearchParam = (paramName: string): ValueToggleType =>
  useSearchParamState(paramName)[2] as ValueToggleType;

/**
 * Returns a boolean value, a function to toggle it, and a setter to explicitly set it
 */
export const useBooleanSearchParam = (
  paramName: string,
  defaultValue: boolean = false,
  globOptions?: NavigateOptions
): UseBooleanSearchParamReturn => {
  const [value, setValue] = useSearchParamState(
    paramName,
    defaultValue,
    globOptions
  );

  const isOn =
    value === "true" || value === true
      ? true
      : value === "false" || value === false
        ? false
        : defaultValue;

  const setBoolValue: BooleanValueSetterType = useCallback((newValue, options) => {
    // Turning off usually means dropping the param, but with `defaultValue: true` an
    // absent param reads back as ON — that state needs an explicit "false" to stick.
    setValue(newValue ? "true" : defaultValue ? "false" : null, options);
  }, []);

  const toggleBoolValue: ValueToggleType = useCallback((options) => {
    const current = getCurrentSearchParamsObj();
    const currentValue = readPendingOrLiveValue(paramName);
    const currentlyOn =
      currentValue === "true" || (currentValue === null && defaultValue);

    setBoolValue(!currentlyOn, { ...globOptions, ...options });

    return current;
  }, []);

  return [isOn, toggleBoolValue, setBoolValue];
};
